import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { Clock, User, FileText, Plus, Edit, Trash2, Search, Settings2, Eye, Smartphone, Monitor, Cog, Webhook } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACTION_ICONS = {
  CREATE: <Plus className="w-3.5 h-3.5" />,
  UPDATE: <Edit className="w-3.5 h-3.5" />,
  DELETE: <Trash2 className="w-3.5 h-3.5" />,
  LOGIN: <User className="w-3.5 h-3.5" />,
  APPROVE: <Eye className="w-3.5 h-3.5" />,
  PERMISSIONS_UPDATE: <Settings2 className="w-3.5 h-3.5" />,
};

const ACTION_COLORS = {
  CREATE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  UPDATE: "bg-blue-100 text-blue-700 border-blue-200",
  DELETE: "bg-red-100 text-red-700 border-red-200",
  LOGIN: "bg-purple-100 text-purple-700 border-purple-200",
  APPROVE: "bg-amber-100 text-amber-700 border-amber-200",
  PERMISSIONS_UPDATE: "bg-indigo-100 text-indigo-700 border-indigo-200",
};

const MODULE_LABELS = {
  users: "Users", dealers: "Dealers", employees: "Employees", products: "Products", 
  orders: "Orders", collections: "Collections", auth: "Authentication",
  factory_production: "Factory Production", factory_equipment: "Equipment",
};

const SOURCE_CONFIG = {
  mobile: { icon: Smartphone, color: "bg-blue-100 text-blue-700 border-blue-200", label: "Mobile" },
  admin: { icon: Monitor, color: "bg-slate-100 text-slate-700 border-slate-200", label: "Admin" },
  system: { icon: Cog, color: "bg-purple-100 text-purple-700 border-purple-200", label: "System" },
  api: { icon: Webhook, color: "bg-teal-100 text-teal-700 border-teal-200", label: "API" },
};

const SourceBadge = ({ source = "admin" }) => {
  const cfg = SOURCE_CONFIG[source] || SOURCE_CONFIG.admin;
  const Icon = cfg.icon;
  return (
    <Badge className={`text-[10px] px-1.5 py-0 gap-0.5 ${cfg.color}`} data-testid={`audit-source-${source}`}>
      <Icon className="w-2.5 h-2.5" /> {cfg.label}
    </Badge>
  );
};

const AuditTrailPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await authFetch(`${API}/audit-logs?limit=200`);
        const data = await res.json();
        if (data.success) setLogs(data.data || []);
      } catch { toast.error("Failed to load audit logs"); }
      finally { setLoading(false); }
    };
    fetchLogs();
  }, []);

  const filteredLogs = logs.filter(log => {
    if (moduleFilter !== "all" && log.module !== moduleFilter) return false;
    if (actionFilter !== "all" && log.action !== actionFilter) return false;
    if (sourceFilter !== "all" && (log.source || "admin") !== sourceFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (log.actor_name || "").toLowerCase().includes(q) ||
        (log.entity_name || "").toLowerCase().includes(q) ||
        (log.module || "").toLowerCase().includes(q);
    }
    return true;
  });

  const uniqueModules = [...new Set(logs.map(l => l.module))];
  const uniqueActions = [...new Set(logs.map(l => l.action))];

  const formatDetails = (log) => {
    if (!log.details || typeof log.details !== "object") return null;
    const entries = Object.entries(log.details).filter(([k]) => !["_id", "password_hash"].includes(k));
    if (entries.length === 0) return null;
    return entries.map(([k, v]) => (
      <span key={k} className="text-[10px] text-slate-400 mr-3">
        <span className="text-slate-500">{k.replace(/_/g, " ")}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}
      </span>
    ));
  };

  return (
    <div className="space-y-6" data-testid="audit-trail-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Audit Trail</h1>
        <p className="text-slate-500 text-sm mt-1">Track all changes: who created, updated, or deleted what</p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 w-56" data-testid="audit-search" />
        </div>
        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger className="w-40" data-testid="module-filter"><SelectValue placeholder="All Modules" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modules</SelectItem>
            {uniqueModules.map(m => <SelectItem key={m} value={m}>{MODULE_LABELS[m] || m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-40" data-testid="action-filter"><SelectValue placeholder="All Actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {uniqueActions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-40" data-testid="source-filter"><SelectValue placeholder="All Sources" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="admin">Admin Panel</SelectItem>
            <SelectItem value="mobile">Mobile App</SelectItem>
            <SelectItem value="system">System</SelectItem>
            <SelectItem value="api">API / Webhook</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-400 ml-auto">{filteredLogs.length} entries</span>
      </div>

      <Card className="overflow-hidden">
        <div className="max-h-[65vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-12"><div className="w-6 h-6 spinner mx-auto" /></div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              <p>No audit logs found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredLogs.map((log, i) => (
                <div key={i} className="px-4 py-3 hover:bg-slate-50/50 transition-colors" data-testid={`audit-log-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${ACTION_COLORS[log.action] || "bg-slate-100 text-slate-600"}`}>
                      {ACTION_ICONS[log.action] || <FileText className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-900">{log.actor_name || "System"}</span>
                        <Badge className={`text-[10px] px-1.5 py-0 ${ACTION_COLORS[log.action] || "bg-slate-100 text-slate-600"}`}>
                          {log.action}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {MODULE_LABELS[log.module] || log.module}
                        </Badge>
                        <SourceBadge source={log.source || "admin"} />
                        {log.entity_name && (
                          <span className="text-xs text-slate-600 font-medium">&quot;{log.entity_name}&quot;</span>
                        )}
                      </div>
                      {formatDetails(log) && (
                        <div className="mt-1 flex flex-wrap gap-x-1">{formatDetails(log)}</div>
                      )}
                      <p className="text-[10px] text-slate-400 mt-1">
                        {new Date(log.timestamp).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        {log.actor_id && <span className="ml-2 text-slate-300">ID: {log.actor_id}</span>}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default AuditTrailPage;
